import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  startCreateAdsCampaignFolder,
  startListenerAdsCampaignFolders,
  startMoveAdsCampaignsToFolder,
  startRenameAdsCampaignFolder,
  startUnassignAdsCampaignsFromFolder,
  stopListenerAdsCampaignFolders,
  startDeleteAdsCampaignFolder,
} from "../actions/adsCampaignFolders";
import { SimpleAlert } from "../components/Alerts/Alerts";
import { useTranslationApp } from "../lib/i18next";
import { useDispatchApp } from "../lib/redux";
import { useConfirmDeleteFolder } from "./useConfirmDeleteFolder";
import {
  CAMPAIGNS_FOLDERS_MOVE_ERROR,
  CAMPAIGNS_FOLDERS_MOVE_SUCCESS,
  CAMPAIGNS_FOLDERS_UNASSIGN_ERROR,
  CAMPAIGNS_FOLDERS_UNASSIGN_SUCCESS,
  ERROR,
} from "../i18n/keysTranslations";
import { ALL_FILTER, CONTENT_FOLDER_ROOT_ID } from "../utils/constants";
import { getFolderPathLabel } from "../utils/contentFolderUtils";

export const useAdsCampaignFoldersSession = ({
  folderFilterID,
  onFolderFilterChange,
  setCampaigns,
  clearSelection = () => {},
  getPreviousFolderID = () => null,
  onFolderMembershipChange = () => {},
}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [foldersByID, setFoldersByID] = useState({});
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isMovingContents, setIsMovingContents] = useState(false);
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [isOpenCreateFolderModal, setIsOpenCreateFolderModal] = useState(false);
  const [isOpenMoveFolderModal, setIsOpenMoveFolderModal] = useState(false);
  const [isOpenRenameFolderModal, setIsOpenRenameFolderModal] = useState(false);
  const [renameFolderTarget, setRenameFolderTarget] = useState(null);
  const listenerRef = useRef(null);

  const activeFolderID = folderFilterID === ALL_FILTER ? null : folderFilterID;

  useEffect(() => {
    setIsLoadingFolders(true);
    dispatch(
      startListenerAdsCampaignFolders({
        onUpdatedDataCallback: (folders) => {
          setFoldersByID(folders || {});
          setIsLoadingFolders(false);
        },
        onReferenceAvailable: (unsubscribe) => {
          listenerRef.current = unsubscribe;
        },
      })
    ).then((started) => {
      if (!started) setIsLoadingFolders(false);
    });

    return () => {
      dispatch(
        stopListenerAdsCampaignFolders({
          unsubscribe: listenerRef.current,
        })
      );
      listenerRef.current = null;
    };
  }, [dispatch]);

  const folders = useMemo(() => {
    return Object.entries(foldersByID)
      .map(([id, folder]) => ({
        id,
        ...folder,
      }))
      .sort((left, right) => right.creationTime - left.creationTime);
  }, [foldersByID]);

  const getFolderLabel = useCallback(
    ({ folderID }) => getFolderPathLabel({ folders, folderID }),
    [folders]
  );

  const onCreateFolder = useCallback(
    async ({ name, parentFolderID }) => {
      setIsCreatingFolder(true);
      try {
        return await dispatch(
          startCreateAdsCampaignFolder({ name, parentFolderID })
        );
      } finally {
        setIsCreatingFolder(false);
      }
    },
    [dispatch]
  );

  const applyFolderChangeToLocalData = useCallback(
    ({ campaignIDs, folderID }) => {
      const movedCampaignIDs = new Set(campaignIDs.map(String));

      setCampaigns((currentCampaigns) => {
        const nextCampaigns = { ...(currentCampaigns || {}) };

        Object.entries(nextCampaigns).forEach(([key, item]) => {
          const itemID = String(item?.id || key);
          if (!movedCampaignIDs.has(itemID)) return;

          if (activeFolderID && folderID !== activeFolderID) {
            delete nextCampaigns[key];
            return;
          }

          if (folderID) {
            nextCampaigns[key] = { ...item, folderID };
            return;
          }

          const nextItem = { ...item };
          delete nextItem.folderID;
          nextCampaigns[key] = nextItem;
        });

        return nextCampaigns;
      });
      clearSelection();
    },
    [activeFolderID, clearSelection, setCampaigns]
  );

  const toCampaignRecords = useCallback(
    (campaignIDs) =>
      campaignIDs.map((campaignID) => ({
        campaignID: String(campaignID),
        previousFolderID: getPreviousFolderID(campaignID) || null,
      })),
    [getPreviousFolderID]
  );

  const applyMoveContents = useCallback(
    async ({ campaignIDs, folderID }) => {
      if (!campaignIDs?.length) {
        return { ok: false, reason: "noChanges" };
      }

      const campaignRecords = toCampaignRecords(campaignIDs);
      setIsMovingContents(true);
      try {
        const response = folderID
          ? await dispatch(
              startMoveAdsCampaignsToFolder({ campaignRecords, folderID })
            )
          : await dispatch(
              startUnassignAdsCampaignsFromFolder({ campaignRecords })
            );

        if (!response?.ok) {
          return response;
        }

        const movedCampaignIDs = response.data.campaignIDs;
        const nextFolderID = folderID || null;
        applyFolderChangeToLocalData({
          campaignIDs: movedCampaignIDs,
          folderID: nextFolderID,
        });
        onFolderMembershipChange({
          campaignIDs: movedCampaignIDs,
          folderID: nextFolderID,
        });

        return response;
      } finally {
        setIsMovingContents(false);
      }
    },
    [
      applyFolderChangeToLocalData,
      dispatch,
      onFolderMembershipChange,
      toCampaignRecords,
    ]
  );

  const onDropMany = useCallback(
    async ({ contentIDs, folderID }) => {
      if (!contentIDs?.length) return;

      const response = await applyMoveContents({
        campaignIDs: contentIDs,
        folderID,
      });

      if (!response?.ok) {
        if (response?.reason === "noChanges") return;

        await SimpleAlert({
          icon: "error",
          title: t(ERROR),
          text: t(
            folderID
              ? CAMPAIGNS_FOLDERS_MOVE_ERROR
              : CAMPAIGNS_FOLDERS_UNASSIGN_ERROR
          ),
        });
        return;
      }

      await SimpleAlert({
        icon: "success",
        title: t(
          folderID
            ? CAMPAIGNS_FOLDERS_MOVE_SUCCESS
            : CAMPAIGNS_FOLDERS_UNASSIGN_SUCCESS
        ),
      });
    },
    [applyMoveContents, t]
  );

  const onRenameFolder = useCallback(
    async ({ folderID, name }) => {
      setIsRenamingFolder(true);
      try {
        return await dispatch(startRenameAdsCampaignFolder({ folderID, name }));
      } finally {
        setIsRenamingFolder(false);
      }
    },
    [dispatch]
  );

  const onSelectFolder = useCallback(
    (folderID) => {
      clearSelection();
      onFolderFilterChange(folderID === null ? ALL_FILTER : folderID);
    },
    [clearSelection, onFolderFilterChange]
  );

  const onCreateFolderInContext = useCallback(
    async ({ name, parentFolderID }) =>
      onCreateFolder({
        name,
        parentFolderID: parentFolderID ?? activeFolderID,
      }),
    [activeFolderID, onCreateFolder]
  );

  const openCreateFolderModal = useCallback(() => {
    setIsOpenCreateFolderModal(true);
  }, []);

  const closeCreateFolderModal = useCallback(() => {
    setIsOpenCreateFolderModal(false);
  }, []);

  const openMoveFolderModal = useCallback(() => {
    setIsOpenMoveFolderModal(true);
  }, []);

  const closeMoveFolderModal = useCallback(() => {
    setIsOpenMoveFolderModal(false);
  }, []);

  const openRenameFolderModal = useCallback((folder) => {
    setRenameFolderTarget(folder);
    setIsOpenRenameFolderModal(true);
  }, []);

  const closeRenameFolderModal = useCallback(() => {
    setIsOpenRenameFolderModal(false);
    setRenameFolderTarget(null);
  }, []);

  const createFolderParentID = activeFolderID ?? CONTENT_FOLDER_ROOT_ID;
  const createFolderParentLabel = activeFolderID
    ? getFolderLabel({ folderID: activeFolderID })
    : null;

  const deleteFolder = useCallback(
    async ({ folderID }) => {
      return dispatch(startDeleteAdsCampaignFolder({ folderID }));
    },
    [dispatch]
  );

  const onFolderDeleted = useCallback(
    ({ folder, response }) => {
      if (activeFolderID === folder.id) onSelectFolder(null);
      if (response?.data?.contentIDs?.length) {
        const campaignIDs = response.data.contentIDs;
        applyFolderChangeToLocalData({ campaignIDs, folderID: null });
        onFolderMembershipChange({ campaignIDs, folderID: null });
      }
    },
    [
      activeFolderID,
      onSelectFolder,
      applyFolderChangeToLocalData,
      onFolderMembershipChange,
    ]
  );

  const onDeleteFolder = useConfirmDeleteFolder({
    deleteFolder,
    onDeleted: onFolderDeleted,
  });

  return {
    folders,
    activeFolderID,
    isLoadingFolders,
    isCreatingFolder,
    isMovingContents,
    isRenamingFolder,
    onSelectFolder,
    onDropMany,
    moveContents: applyMoveContents,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    isOpenMoveFolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
    isOpenRenameFolderModal,
    openRenameFolderModal,
    closeRenameFolderModal,
    renameFolderTarget,
    onRenameFolder,
    onDeleteFolder,
  };
};
